
import React from 'react';
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Trip } from "@/types/trip";
import { TimelineAdDialog } from "@/components/timeline/TimelineAdDialog";
import { useTimelineAd } from "@/hooks/use-timeline-ad";
import { TripList } from "@/components/timeline/TripList";
import { EmptyStateDialog } from "@/components/timeline/EmptyStateDialog";
import { Gift, Navigation, Bell, UserCircle2, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { parseISO, isAfter, compareAsc, format } from 'date-fns';
import { useIsMobile } from "@/hooks/use-mobile";
import { ScrollArea } from "@/components/ui/scroll-area";
import Hey from '@/lib/hey-utils';

interface TimelineProps {
  trips: Trip[];
  handleRefreshTrips: () => void;
}
export const Timeline: React.FC<TimelineProps> = ({
  trips,
  handleRefreshTrips
}) => {
  const {
    t,
    i18n
  } = useTranslation();
  const timelineAdQuery = useTimelineAd(trips);
  const showAd = timelineAdQuery.data?.imageUrl && timelineAdQuery.isSuccess;
  const adIndex = trips.findIndex(trip => trip.id === timelineAdQuery.data?.tag);
  const closeAd = () => {
    if (typeof timelineAdQuery.refetch === 'function') {
      timelineAdQuery.refetch();
    }
  };
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const now = new Date();

  const separateTrips = () => {
    try {
      const past: Trip[] = [];
      const future: Trip[] = [];
      trips.forEach(trip => {
        if (trip.elements && trip.elements.length > 0) {
          const lastElement = trip.elements[trip.elements.length - 1];
          if (lastElement && lastElement.endDate) {
            const endDate = parseISO(lastElement.endDate || "1970");
            if (isAfter(endDate, now)) {
              future.push(trip);
            } else {
              past.push(trip);
            }
          } else {
            future.push(trip);
          }
        } else {
          future.push(trip);
        }
      });
      past.sort((a, b) => {
        try {
          const aDate = a.elements?.length > 0 ? parseISO(a.elements[a.elements.length - 1].endDate || "1970") : new Date(0);
          const bDate = b.elements?.length > 0 ? parseISO(b.elements[b.elements.length - 1].endDate || "1970") : new Date(0);
          return compareAsc(aDate, bDate);
        } catch (error) {
          console.error('Error sorting past trips:', error);
          return 0;
        }
      });
      future.sort((a, b) => {
        try {
          const aDate = a.elements?.length > 0 ? parseISO(a.elements[0].startDate || "1970") : new Date();
          const bDate = b.elements?.length > 0 ? parseISO(b.elements[0].startDate || "1970") : new Date();
          return compareAsc(aDate, bDate);
        } catch (error) {
          console.error('Error sorting future trips:', error);
          return 0;
        }
      });
      return {
        pastTrips: past,
        futureTrips: future
      };
    } catch (error) {
      console.error('Error separating trips:', error);
      return {
        pastTrips: [],
        futureTrips: trips
      };
    }
  };

  const {
    pastTrips,
    futureTrips
  } = separateTrips();

  const renderEmptyState = () => {
    const features = [{
      icon: <Bell className="w-6 h-6 text-purple-500" />,
      title: t("index.welcome.features.tripAlerts.title", "Trip Alerts"),
      description: t("index.welcome.features.tripAlerts.description", "Receive timely notifications about your flights, gate changes, and important travel updates.")
    }, {
      icon: <Gift className="w-6 h-6 text-pink-500" />,
      title: t("index.welcome.features.offers.title"),
      description: t("index.welcome.features.offers.description")
    }];
    const tips = [{
      icon: <UserCircle2 className="w-5 h-5 text-indigo-500" />,
      text: t("index.welcome.tips.profile")
    }, {
      icon: <Navigation className="w-5 h-5 text-blue-500" />,
      text: t("index.welcome.tips.addTrip")
    }, {
      icon: <Bell className="w-5 h-5 text-amber-500" />,
      text: t("index.welcome.tips.notifications")
    }];
    
    return (
      <>
        <ScrollArea className={`w-full px-4 max-w-3xl mx-auto ${isMobile ? 'pb-28' : 'pb-36'}`}>
          <div className="sticky top-0 z-10 bg-white/90 backdrop-blur-sm w-full py-4 px-4 border-b">
            <div className="text-center mb-8">
              <div className="inline-block p-3 bg-travel-50 rounded-full mb-4">
                <img src={Hey.HeyUtils.getInstance().getConfig().clientLogoUrl} alt="App Logo" className="w-8 h-8" />
              </div>
              <h2 className="text-2xl font-bold mb-3">{t("index.welcome.title")}</h2>
              <p className="text-gray-600">{t("index.welcome.ready")}</p>
            </div>
            
            <div className="flex flex-col items-center gap-4 max-w-3xl mx-auto">
              <div className="p-4 rounded-lg bg-gradient-to-br from-white to-gray-50 shadow-sm hover:shadow-md transition-shadow w-full">
                <div className="flex flex-col items-center text-center space-y-3">
                  <div className="p-3 rounded-full bg-white shadow-inner">
                    <Gift className="w-6 h-6 text-pink-500" />
                  </div>
                  <h3 className="font-semibold text-lg">{t("index.welcome.features.tripManagement.title")}</h3>
                  <p className="text-sm text-gray-600 mb-4">{t("index.welcome.features.tripManagement.description")}</p>
                  <Button onClick={() => navigate('/new-trip')} className="bg-[#3a8bff]">
                    <Plus className="w-4 h-4 mr-2" />
                    {t("index.addTrip")}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {features.map((feature, index) => <div key={index} className="p-4 rounded-lg bg-gradient-to-br from-white to-gray-50 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex flex-col items-center text-center space-y-3">
                  <div className="p-3 rounded-full bg-white shadow-inner">
                    {feature.icon}
                  </div>
                  <h3 className="font-semibold text-lg">{feature.title}</h3>
                  <p className="text-sm text-gray-600">{feature.description}</p>
                </div>
              </div>)}
          </div>

          <div className="bg-travel-50/50 rounded-lg p-6 mb-8">
            <h3 className="font-semibold text-lg mb-4 text-center">
              {t("index.welcome.getStarted", "Quick Tips to Get Started")}
            </h3>
            <div className="space-y-4">
              {tips.map((tip, index) => <div key={index} className="flex items-center space-x-3 bg-white p-3 rounded-md shadow-sm">
                  <div className="flex-shrink-0">{tip.icon}</div>
                  <p className="text-sm text-gray-700">{tip.text}</p>
                </div>)}
            </div>
          </div>
        </ScrollArea>
      </>
    );
  };

  return <div className={`relative flex flex-col ${!isMobile ? "h-auto" : ""}`} style={{
    paddingRight: "20px",
  }}>
      {trips.length === 0 ? renderEmptyState() : <div className={`relative ${isMobile ? "flex-1 w-full overflow-x-auto scrollbar-hide" : "w-full"}`}>
          <TripList futureTrips={futureTrips} pastTrips={pastTrips} isTimelineVisible={true} handleRefreshTrips={handleRefreshTrips} />
        </div>}

      {adIndex !== -1 && showAd && <TimelineAdDialog open={!!showAd} onOpenChange={closeAd} imageUrl={timelineAdQuery.data?.imageUrl || ''} link={timelineAdQuery.data?.link || ''} onImpression={() => console.debug('Ad impression recorded')} />}
      <EmptyStateDialog open={false} onOpenChange={() => {}} />
    </div>;
};
